import { useEffect, useState } from 'react';
import { SCORER_MOCKS } from '../../shared/mocks.ts';
import { type AdminFixtures, api } from '../lib/api.ts';
import { cn } from '../lib/cn.ts';
import { useInLab } from '../shell/inLab.ts';
import { anyMock, clearMocks, type Mocks, setMocks, useMocks } from '../shell/mocks.ts';

const SCORER_LABELS: Record<(typeof SCORER_MOCKS)[number], string> = {
  fake: 'Recorded scores',
  off_topic: 'Not scored: off topic',
  unclear: 'Not scored: unclear',
  repeated: 'Not scored: question read back',
  capped: 'Not scored: daily cap',
  unavailable: 'Not scored: fleet down',
};

/** Admin mocks for this tab: what the next call and answers use instead of Gemini, the mic, the fleet, the review. */
const LAB_FIXTURES: AdminFixtures = {
  sessions: ['amazon-pm', 'call1-pilot'],
  audio: ['interview-answer', 'intro-answer'],
};
const NO_MOCKS: Mocks = { replay: null, audio: null, scorer: null, review: null };

export function MocksPanel({ preview }: { preview?: Mocks }) {
  const stored = useMocks();
  // In the Lab: fixed fixtures and mocks, no fetch, no writes to this tab's real mocks.
  const inLab = useInLab();
  const mocks = inLab ? (preview ?? NO_MOCKS) : stored;
  const [fixtures, setFixtures] = useState<AdminFixtures | null>(inLab ? LAB_FIXTURES : null);
  useEffect(() => {
    if (!inLab) api.adminFixtures().then(setFixtures, () => setFixtures({ sessions: [], audio: [] }));
  }, [inLab]);
  const write = (next: Partial<Mocks>) => (inLab ? undefined : setMocks(next));

  const select = <K extends keyof Mocks>(
    key: K,
    label: string,
    options: Array<[NonNullable<Mocks[K]>, string]>,
    off: string,
  ) => (
    <label className="flex flex-col gap-1 text-sm text-muted">
      {label}
      <select
        data-testid={`mock-${key}`}
        value={mocks[key] ?? ''}
        onChange={(e) => write({ [key]: e.target.value || null } as Partial<Mocks>)}
        className="min-h-11 rounded-xl bg-fg/5 px-3 text-[15px] text-fg ring-1 ring-fg/15 focus:outline-none focus:ring-accent/50"
      >
        <option value="">{off}</option>
        {/* A stored choice the server no longer lists stays visible, instead of the select showing "off". */}
        {mocks[key] && !options.some(([v]) => v === mocks[key]) ? (
          <option value={mocks[key] ?? ''}>{`${mocks[key]} (not available)`}</option>
        ) : null}
        {options.map(([value, text]) => (
          <option key={value} value={value}>
            {text}
          </option>
        ))}
      </select>
    </label>
  );

  return (
    <div data-testid="mocks-panel" className="space-y-3 border-t border-fg/10 pt-4">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-sm font-medium text-fg">Mocks</h3>
        {anyMock(mocks) ? (
          <button
            type="button"
            data-testid="mocks-clear"
            onClick={() => (inLab ? undefined : clearMocks())}
            className="min-h-11 text-sm text-accent hover:underline"
          >
            Clear all
          </button>
        ) : null}
      </div>
      <p className="text-sm text-muted">
        For this tab, on the test account. The next call and answers use them, and what they produce is saved like the
        real thing (a reset starts it clean).
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        {select(
          'replay',
          'Live call',
          (fixtures?.sessions ?? []).map((s) => [s, `Replay ${s}`]),
          'Gemini (real)',
        )}
        {select(
          'audio',
          'Answer audio',
          (fixtures?.audio ?? []).map((a) => [a, `Clip ${a}`]),
          'Microphone',
        )}
        {select(
          'scorer',
          'Speech scores',
          SCORER_MOCKS.map((s) => [s, SCORER_LABELS[s]]),
          'Scoring fleet (real)',
        )}
        {select('review', 'Answer review', [['fail', 'Fails']], 'Gemini (real)')}
      </div>
      {mocks.replay ? (
        <p className="text-sm text-muted">
          A replay plays a recorded call, so its names, praise and promises come from that recording.
        </p>
      ) : null}
      {mocks.audio ? (
        <p className="text-sm text-muted">
          The mic is off for the whole call: type to talk. The clip answers every speaking task.
        </p>
      ) : null}
    </div>
  );
}

/**
 * What isn't real right now: the admin's test account (ADR-0014) and any mocks. On screen for a whole call, so a mocked
 * one is never mistaken for a real one, and at the top of every other page.
 */
export function MocksBadge({
  mocks,
  sandbox = false,
  onOpen,
  inCall,
}: {
  mocks: Mocks | null;
  sandbox?: boolean;
  onOpen?: () => void;
  inCall: boolean;
}) {
  const parts = [
    mocks?.replay && 'replay',
    mocks?.audio && 'clip',
    mocks?.scorer && (mocks.scorer === 'fake' ? 'recorded scores' : mocks.scorer.replace('_', ' ')),
    mocks?.review && 'review fails',
  ].filter(Boolean);
  // It sits in the top padding every screen keeps (the call header's, pt-10 elsewhere), so it covers nothing and adds
  // no height. Fixed during a call, where cards scroll under the header; elsewhere it scrolls away with the page
  // rather than float over what scrolls under it.
  const className = cn(
    'left-1/2 top-2 z-40 max-w-[calc(100%-2rem)] -translate-x-1/2 truncate rounded-full bg-surface px-3 py-1 text-xs font-medium text-warn shadow-sm ring-1 ring-warn/40',
    inCall ? 'fixed' : 'absolute',
  );
  const text = [sandbox ? 'Test account' : null, parts.length ? `Mocks: ${parts.join(' · ')}` : null]
    .filter(Boolean)
    .join(' · ');
  // During a call Profile can't open (the call stays on screen), so the badge is only a label then.
  return onOpen && !inCall ? (
    <button type="button" data-testid="mocks-badge" onClick={onOpen} className={className}>
      {text}
    </button>
  ) : (
    <span data-testid="mocks-badge" className={className}>
      {text}
    </span>
  );
}
